import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Field, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';

import { Box, Button, CircularProgress, Grid, IconButton, InputLabel, TextField, Tooltip } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import DeleteIcon from '@material-ui/icons/Delete';
import Alert from '@material-ui/lab/Alert';

import DialogComponent from 'app/components/DialogComponent';
import FileField from 'app/components/FileField';
import certificationEndpoints from 'app/api/services/certificationEndpoints';

import { errorHandler } from 'app/utils/errorHandler';
import notification from 'app/config/notification';

import { COMPANY_SERVICES, COMPANY_TYPES } from 'app/utils/constants';

function Modal(props) {
    const [changingDate, setChangingDate] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const {
        classes,
        open,
        onClose,
        certification,
        request,
        requestJsonData,
        onSave,
        downloadFile,
        updateFile,
        setUpdateFile,
    } = props;

    const [certNumber] = useState(() => {
        if (request.cdEmpresa) {
            return request.cdEmpresa;
        } else if (requestJsonData.cdEmpresa) {
            return requestJsonData.cdEmpresa;
        }
        return '';
    });

    const [initialEmissionDate, setInitialEmissionDate] = useState(null);
    const [initialExpirationDate, setInitialExpirationDate] = useState(null);

    const dispatch = useDispatch();

    useEffect(() => {
        (async () => {
            try {
                let response = await certificationEndpoints.getEmissionDate({
                    cdSolicitacao: request.cdSolicitacao,
                    cdTipoEmpresa: Number(requestJsonData.cdTipoEmpresa),
                    cdEmpresa: request.cdEmpresa,
                    numeroCrc: certNumber ? certNumber : null,
                });
                if (response?.status === 200 && response?.data) setInitialEmissionDate(moment.utc(response.data));
                response = await certificationEndpoints.getExpirationDate({
                    cdSolicitacao: request.cdSolicitacao,
                    cdTipoEmpresa: Number(requestJsonData.cdTipoEmpresa),
                    dtEmissaoCrc: moment.utc(new Date()).format('YYYY-MM-DD'),
                    numeroCrc: certNumber ? certNumber : null,
                });
                if (response?.status === 200 && response?.data) setInitialExpirationDate(moment.utc(response.data));
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChangeDate = async (values, field, value) => {
        setChangingDate(changingDate + 1);
        values[field] = value;

        if (field === 'dtEmissaoCrc') {
            if (moment.utc(value).isValid()) {
                setLoading(true);
                const v = await searchExpirationDate(value);
                await onChangeDate(values, 'dtVencimentoCrc', v);
                setInitialExpirationDate(v);
                setLoading(false);
            }
        }
    };

    const searchExpirationDate = async (createDate) => {
        try {
            const response = await certificationEndpoints.getExpirationDate({
                cdSolicitacao: request.cdSolicitacao,
                cdTipoEmpresa: Number(requestJsonData.cdTipoEmpresa),
                dtEmissaoCrc: moment.utc(createDate).format('YYYY-MM-DD'),
                numeroCrc: certNumber ? certNumber : null,
            });
            if (response.status === 200 && response.data) {
                return moment.utc(response.data);
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        return null;
    };

    const onCancel = () => {
        setUpdateFile(false);
        onClose();
    };

    const deleteFile = (values) => {
        setUpdateFile(true);
        values.updateFile = true;
    };

    const handleSave = async (values) => {
        if (values.idFileCrc && !updateFile) {
            notification('Para alterar dados do certificado, é necessário remover o certificado atual.', {
                toastId: 'DELETE_FILE_NEEDED',
                type: 'warning',
            });
            return;
        }
        setSending(true);
        if (!values.dtVencimentoCrc || values.dtVencimentoCrc === '') {
            values.dtVencimentoCrc = await searchExpirationDate(values);
        }
        await onSave(values);
        setSending(false);
    };

    return (
        <DialogComponent title={'Detalhamento do certificado'} open={open} onClose={onClose}>
            <Formik
                initialValues={certification ? certification : {}}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={(values) => {
                    handleSave(values);
                }}>
                {({ values }) => {
                    return (
                        <Form className={classes.containerForm}>
                            <Grid container spacing={1} className={classes.fieldsContainer}>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel>Número</InputLabel>
                                    <Field
                                        name=""
                                        disabled
                                        variant="filled"
                                        margin="dense"
                                        value={certNumber}
                                        fullWidth
                                        as={TextField}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={1} className={classes.fieldsContainer}>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Empresa</InputLabel>
                                    <Field
                                        name="deEmpresa"
                                        disabled
                                        variant="filled"
                                        margin="dense"
                                        fullWidth
                                        value={
                                            requestJsonData.cdTipoPessoaFisica
                                                ? requestJsonData.nmCompleto
                                                : requestJsonData.deRazaoSocial
                                        }
                                        as={TextField}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Emissão</InputLabel>
                                    <Field
                                        name="dtEmissaoCrc"
                                        disabled={values.idFileCrc && !updateFile}
                                        disableToolbar
                                        format="DD/MM/YYYY"
                                        emptyLabel={''}
                                        autoOk
                                        clearable
                                        clearLabel={'Limpar'}
                                        value={
                                            values.dtEmissaoCrc
                                                ? moment.utc(values.dtEmissaoCrc).format('YYYY-MM-DD')
                                                : initialEmissionDate
                                        }
                                        invalidLabel="Data inválida"
                                        invalidDateMessage="Data inválida"
                                        maxDateMessage="Data inválida"
                                        minDateMessage="Data inválida"
                                        margin="dense"
                                        onChange={(v) => onChangeDate(values, 'dtEmissaoCrc', v)}
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date',
                                        }}
                                        inputVariant="filled"
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        as={KeyboardDatePicker}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    style={{
                                        display:
                                            requestJsonData.cdTipoEmpresa &&
                                            parseInt(requestJsonData.cdTipoEmpresa) === COMPANY_TYPES.GAS &&
                                            COMPANY_SERVICES.includes(request.cdServico)
                                                ? 'none'
                                                : 'block',
                                    }}>
                                    <InputLabel>Vencimento</InputLabel>
                                    {loading && (
                                        <div className={classes.progress}>
                                            <CircularProgress size={20} />
                                        </div>
                                    )}
                                    {!loading && (
                                        <Field
                                            name="dtVencimentoCrc"
                                            disabled={values.idFileCrc && !updateFile}
                                            disableToolbar
                                            format="DD/MM/YYYY"
                                            emptyLabel={''}
                                            autoOk
                                            clearable
                                            clearLabel={'Limpar'}
                                            value={
                                                values.dtVencimentoCrc
                                                    ? moment.utc(values.dtVencimentoCrc).format('YYYY-MM-DD')
                                                    : initialExpirationDate
                                            }
                                            invalidLabel="Data inválida"
                                            invalidDateMessage="Data inválida"
                                            maxDateMessage="Data inválida"
                                            minDateMessage="Data inválida"
                                            margin="dense"
                                            onChange={(v) => onChangeDate(values, 'dtVencimentoCrc', v)}
                                            KeyboardButtonProps={{
                                                'aria-label': 'change date',
                                            }}
                                            inputVariant="filled"
                                            fullWidth
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            as={KeyboardDatePicker}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <InputLabel>Observações</InputLabel>
                                    <Field
                                        name="deObservacao"
                                        disabled={values.idFileCrc && !updateFile}
                                        variant="filled"
                                        margin="dense"
                                        multiline
                                        value={values.deObservacao ? values.deObservacao : ''}
                                        minRows={4}
                                        fullWidth
                                        as={TextField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                {values.idFileCrc && !updateFile && (
                                    <>
                                        <Grid item xs={12} className={classes.containerIcons}>
                                            <Tooltip title="Excluir arquivo atual e gerar novo">
                                                <IconButton
                                                    className={classes.btnDeleteFile}
                                                    size="small"
                                                    onClick={() => deleteFile(values)}>
                                                    <DeleteIcon className={classes.iconDelete} />
                                                </IconButton>
                                            </Tooltip>
                                            <Button
                                                color="primary"
                                                className={classes.btnActualFile}
                                                component="span"
                                                disableElevation
                                                onClick={async () =>
                                                    await downloadFile({ idFile: values.idFileCrc, dispatch })
                                                }>
                                                <InsertDriveFileIcon className={classes.icon} fontSize="inherit" /> Ver
                                                arquivo atual
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Alert severity="warning">
                                                Atenção: Para alterar dados do certificado, é necessário remover o
                                                certificado atual.
                                            </Alert>
                                        </Grid>
                                    </>
                                )}
                                {(!values.idFileCrc || updateFile) && (
                                    <Grid item xs={12} sm={6} md={6}>
                                        <Field
                                            value={{}}
                                            required={true}
                                            name={`doc-1`}
                                            label={'Carregar arquivo'}
                                            id={`doc-1`}
                                            component={FileField}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container className={classes.actionsContainer}>
                                {sending && (
                                    <Grid item xs={12} className={classes.centerProgress}>
                                        <CircularProgress size={20} />
                                    </Grid>
                                )}
                                {!sending && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <Button className={classes.actionsBtns} onClick={() => onCancel()}>
                                                <FormattedMessage id="LABELS.COMMONS.CANCEL" />
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={4} className={classes.textCenter}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className={classes.actionsBtns}
                                                onClick={() => handleSave(values)}>
                                                <Box className={classes.colorWhite}>Salvar / Gerar </Box>
                                            </Button>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Form>
                    );
                }}
            </Formik>
        </DialogComponent>
    );
}

export default injectIntl(Modal);
